import { css, styled } from 'next-yak';

import { Button } from '@xing-com/button';
import { mediaConfined, zIndexElevatedLayer4 } from '@xing-com/layout-tokens';
import { createPolymorphicElement } from '@xing-com/polymorphic-element';
import {
  cornerRadiusXL,
  dropShadowS,
  motionEasingEnter,
  motionEasingLeave,
  motionTimeM,
  scale005,
  scale150,
  spaceL,
  spaceXL,
  xdlColorBackground,
  xdlColorBackgroundTertiary,
  xdlColorModalBorder,
  xdlColorPremiumTertiary,
  xdlColorProBusinessTertiary,
  xdlColorProJobsTertiary,
} from '@xing-com/tokens';

import type {
  ContentBannerDisplay,
  ContentBannerVariant,
} from './content-banner.types';

type ContentBannerProps = {
  $closingIndicator: boolean;
  $display: ContentBannerDisplay;
  $noPadding?: boolean;
  $variant: ContentBannerVariant;
};

export const contentBannerDuration = motionTimeM;
export const contentBannerTransitionName = 'slide';

// *** Variants ***
export const variants = {
  default: css`
    background-color: ${xdlColorBackgroundTertiary};
  `,
  soft: css`
    background-color: ${xdlColorBackground};
    box-shadow: ${dropShadowS};
  `,
  premium: css`
    background-color: ${xdlColorPremiumTertiary};
  `,
  proJobs: css`
    background-color: ${xdlColorProJobsTertiary};
  `,
  proBusiness: css`
    background-color: ${xdlColorProBusinessTertiary};
  `,
};

// *** Display ***
export const displays = {
  inline: {
    banner: css`
      border-radius: ${cornerRadiusXL};
      position: relative;
      width: 100%;
    `,
    button: css`
      top: 0;
    `,
  },
  fixed: {
    banner: css`
      position: fixed;
      width: 100%;
      z-index: ${zIndexElevatedLayer4};
    `,
    button: css`
      top: 0;

      @media ${mediaConfined} {
        margin-right: ${spaceL};
        top: auto;
      }
    `,
  },
};

// *** Components ***
const ContentBannerElement = createPolymorphicElement('div');

export const ContentBanner = styled(ContentBannerElement)<ContentBannerProps>`
  align-content: center;
  align-items: center;
  display: flex;
  flex-grow: 1;
  padding-bottom: ${spaceXL};
  padding-left: ${spaceL};
  padding-right: ${spaceL};
  padding-top: ${spaceXL};
  position: relative;

  ${({ $variant }) => {
    switch ($variant) {
      case 'default':
        return variants.default;
      case 'soft':
        return variants.soft;
      case 'premium':
        return variants.premium;
      case 'proJobs':
        return variants.proJobs;
      case 'proBusiness':
        return variants.proBusiness;
      default:
        return $variant satisfies undefined;
    }
  }}

  ${({ $display, $closingIndicator, $variant }) => {
    switch ($display) {
      case 'fixed':
        return css`
          ${displays.fixed.banner}

          bottom: 0;
          left: 0;
          position: fixed;
          transform: translate3d(0, 100%, 0);
          z-index: ${zIndexElevatedLayer4};

          /* Animation */
          &.${contentBannerTransitionName}-enter {
            transform: translate3d(0, 99.9%, 0);
          }

          &.${contentBannerTransitionName}-enter-active {
            transform: translate3d(0, 0%, 0);
            transition-duration: ${contentBannerDuration};
            transition-property: transform;
            transition-timing-function: ${motionEasingEnter};
          }

          &.${contentBannerTransitionName}-enter-done {
            transform: translate3d(0, 0%, 0);
            transition-duration: ${contentBannerDuration};
            transition-property: transform;
            transition-timing-function: ${motionEasingEnter};
          }

          &.${contentBannerTransitionName}-exit {
            transform: translate3d(0, 0%, 0);
          }

          &.${contentBannerTransitionName}-exit-active {
            transform: translate3d(0, 99.9%, 0);
            transition-duration: ${contentBannerDuration};
            transition-property: transform;
            transition-timing-function: ${motionEasingLeave};
          }

          &.${contentBannerTransitionName}-exit-done {
            transform: translate3d(0, 100%, 0);
            transition-duration: ${contentBannerDuration};
            transition-property: transform;
            transition-timing-function: ${motionEasingLeave};
          }

          ${$closingIndicator &&
          css`
            /* Keep extra space of icon size + 2x space for fixed version */
            padding-right: calc(${spaceL} + ${scale150} + ${spaceL});
          `};

          ${$variant === 'soft' &&
          css`
            border-top-color: ${xdlColorModalBorder};
            border-top-width: ${scale005};
            border-top-style: solid;
          `};
        `;
      case 'inline':
        return displays.inline.banner;
      default:
        return $display satisfies undefined;
    }
  }}

  ${({ $noPadding }) => {
    return $noPadding
      ? css`
          padding: 0;
        `
      : undefined;
  }}
`;

export const Close = styled(Button)<Pick<ContentBannerProps, '$display'>>`
  && {
    position: absolute;
    right: 0;

    ${({ $display }) => {
      switch ($display) {
        case 'fixed':
          return displays.fixed.button;
        case 'inline':
          return displays.inline.button;
        default:
          return $display satisfies undefined;
      }
    }}
  }
`;
